<template>
    <header class="top" :class="{ 'menu-open': mobileOpen }">
        <div class="top-in">
            <a class="merk" @click="go('dashboard')">
                <img class="merk-logo" src="/x-cube-logo.png" alt="X-Cube" />
                <span class="hub" :class="{ test: isTestEnvironment }">
                    <template v-if="isTestEnvironment">TEST ENV</template>
                    <template v-else>HUB</template>
                    <small>v{{ appVersion }}</small>
                </span>
            </a>

            <nav class="menu" :class="{ open: mobileOpen }" aria-label="Main">
                <div v-for="item in links" :key="item.id || item.route" class="groep">
                    <a
                        v-if="item.type === 'link'"
                        class="menu-knop"
                        :class="{ actief: item.route === selectedRoute }"
                        @click="go(item.route)"
                    >
                        {{ item.label }}
                    </a>
                    <template v-else>
                        <button
                            type="button"
                            class="menu-knop"
                            :class="{ actief: groupContainsRoute(item, selectedRoute) }"
                            :aria-expanded="openId === item.id"
                            @click.stop="toggle(item.id)"
                        >
                            {{ item.label }}                            <span class="pijl-wrap" v-html="ICONS.pijl" />
                        </button>
                        <div v-show="openId === item.id" class="uitklap">
                            <a
                                v-for="child in item.children"
                                :key="child.route"
                                :aria-current="child.route === selectedRoute ? 'page' : null"
                                @click="go(child.route)"
                            >                                <span class="ico" v-html="ICONS[child.icon]" />
                                <span class="t">
                                    <span class="naam">{{ child.label }}</span>
                                    <span class="sub">{{ child.sub }}</span>
                                </span>
                            </a>
                        </div>
                    </template>
                </div>
            </nav>

            <button type="button" class="hamburger" aria-label="Menu" @click.stop="mobileOpen = !mobileOpen">
                <span class="ico" v-html="mobileOpen ? ICONS.kruis : ICONS.bars" />
            </button>

            <div class="top-rechts">
                <a class="nieuw" @click="go('create-booking')">                    <span class="ico" v-html="ICONS.plus" />
                    <span class="lang">New booking</span>
                </a>
                <div class="wie">
                    <button type="button" class="wie-knop" @click.stop="toggle('account')">
                        <span class="avatar">{{ initials }}</span>
                        <span class="mail">{{ userName }}</span>
                    </button>
                    <div v-show="openId === 'account'" class="uitklap rechts">
                        <div class="kop">
                            <b>{{ roleInfo.naam }}</b>
                            <span>{{ roleInfo.sub }}</span>
                        </div>
                        <a v-if="canSeeUsers" @click="go('location')">
                            <span class="ico" v-html="ICONS.user" />
                            <span class="t">
                                <span class="naam">Users</span>
                                <span class="sub">Manage operator accounts</span>
                            </span>
                        </a>
                        <button type="button" class="menu-knop logout-knop" @click="handleLogout">
                            <span class="ico" v-html="ICONS.logout" />Log out
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </header>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import { version } from "../../../package.json";
import { logout } from "../../api/rest";
import { isTestEnvironment as determineIsTestEnvironment } from "../../helpers/auth";
import {
    groupContainsRoute,
    menuForRole,
    ROLE_MANAGEMENT,
    ROLE_OPERATOR,
    ROLE_OPERATOR_ADMIN,
    roleFromUser,
} from "../../helpers/menuLinks";
import useCurrentUserStore from "../../store/userStore";

/** Inline SVGs ported from the x-cube-hub-prototype icon set. */
const ICONS = {
    pijl: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>',
    plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>',
    bars: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M4 7h16M4 12h16M4 17h16"/></svg>',
    kruis: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>',
    users: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="8" r="3.2"/><path d="M2.5 20c0-3.4 2.9-5.5 6.5-5.5s6.5 2.1 6.5 5.5"/><path d="M16 4.6a3.2 3.2 0 0 1 0 6.3M18 14.8c2.1.6 3.5 2.4 3.5 5.2"/></svg>',
    klok: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>',
    hulp: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9.2 9.3a2.8 2.8 0 0 1 5.4 1c0 1.8-2.6 2.2-2.6 3.9"/><path d="M12 17.6h.01"/></svg>',
    user: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="3.6"/><path d="M4.5 20c0-3.6 3.4-6 7.5-6s7.5 2.4 7.5 6"/></svg>',
    boek: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15H6.5A2.5 2.5 0 0 0 4 20.5z"/><path d="M4 20.5A2.5 2.5 0 0 0 6.5 23H20v-5"/></svg>',
    pin: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 21s7-6.2 7-12a7 7 0 0 0-14 0c0 5.8 7 12 7 12Z"/><circle cx="12" cy="9" r="2.5"/></svg>',
    apps: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>',
    spel: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 11h4M8 9v4M15 12h.01M18 10h.01"/><path d="M17.3 5H6.7a4 4 0 0 0-4 3.6l-.6 5.7A2.8 2.8 0 0 0 7 16.4l1.6-1.4h6.8l1.6 1.4a2.8 2.8 0 0 0 4.9-2.1l-.6-5.7A4 4 0 0 0 17.3 5Z"/></svg>',
    power: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 3v8"/><path d="M6.3 6.6a8 8 0 1 0 11.4 0"/></svg>',
    tool: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 3v12M7 10l5 5 5-5M4 21h16"/></svg>',
    logout: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 4h4v16h-4M10 8l-4 4 4 4M6 12h10"/></svg>',
};

/** Role display labels (ported from the prototype account menu). */
const ROLE_INFO = {
    [ROLE_MANAGEMENT]: { naam: "X-Cube management", sub: "X-Corp staff" },
    [ROLE_OPERATOR_ADMIN]: { naam: "Operator Admin", sub: "Own locations · manages operators" },
    [ROLE_OPERATOR]: { naam: "Operator", sub: "Venue staff" },
};

const store = useCurrentUserStore();
const router = useRouter();
const route = useRoute();

/** A single id so only one dropdown (a group or the account menu) is open. */
const openId = ref(null);
const mobileOpen = ref(false);

const currentRole = computed(() => roleFromUser(store.currentUser));
const links = computed(() => menuForRole(currentRole.value));
const selectedRoute = computed(() => route.name);
const isTestEnvironment = computed(() => determineIsTestEnvironment());
const appVersion = computed(() => version);
const userName = computed(() => store.currentUser?.username || "unknown user");
const roleInfo = computed(() => ROLE_INFO[currentRole.value] || ROLE_INFO[ROLE_OPERATOR]);
const canSeeUsers = computed(
    () => currentRole.value === ROLE_MANAGEMENT || currentRole.value === ROLE_OPERATOR_ADMIN,
);

const initials = computed(() => {
    const parts = userName.value.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return userName.value.slice(0, 2).toUpperCase();
});

const toggle = (id) => {
    openId.value = openId.value === id ? null : id;
};

const closeAll = () => {
    openId.value = null;
};

const go = (routeName) => {
    closeAll();
    mobileOpen.value = false;
    if (route.name !== routeName) router.push({ name: routeName });
};

const handleLogout = () => {
    logout();
    router.push("/login");
};

const onDocClick = () => closeAll();
onMounted(() => document.addEventListener("click", onDocClick));
onBeforeUnmount(() => document.removeEventListener("click", onDocClick));
</script>

<style scoped lang="scss">
/* Top bar ported verbatim from x-cube-hub-prototype (src/hub.css `.top`).
   Colours come from the global design tokens on :root. */
.top {
    /* App.vue places .page-wrapper absolutely below a fixed top bar of
       height $hub-nav-height (4rem); keep this bar fixed and the same height. */
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 100;
    background: rgba(14, 7, 37, 0.82);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border-bottom: 1px solid var(--line);
    padding-left: max(16px, env(safe-area-inset-left, 0px));
    padding-right: max(16px, env(safe-area-inset-right, 0px));
    font-family: var(--body);
    /* #app sets text-align:center and font-weight:600 globally; the prototype
       top bar is left-aligned and its base weight is 400 (only menu buttons,
       the brand and item names are heavier via their own rules). */
    text-align: left;
    font-weight: 400;
}
/* The prototype resets box-sizing globally; our port scopes that under `.rd`,
   so re-apply it here or `.menu-knop { width:100% }` + padding overflows the
   mobile panel (a stray horizontal scrollbar). */
.top,
.top * {
    box-sizing: border-box;
}
.top-in {
    display: flex;
    align-items: center;
    gap: var(--s-5);
    height: 64px;
    max-width: 1440px;
    margin: 0 auto;
}

/* brand */
.merk {
    display: flex;
    align-items: center;
    gap: var(--s-3);
    text-decoration: none;
    color: var(--text);
    flex: none;
    cursor: pointer;
}
.merk-logo {
    height: 24px;
    width: auto;
    display: block;
}
.hub {
    font-family: var(--num);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.18em;
    color: var(--muted);
    border-left: 1px solid var(--line-strong);
    padding-left: var(--s-3);
    line-height: 1.3;
}
.hub.test {
    color: var(--amber);
}
.hub small {
    display: block;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: var(--muted-dim);
    font-size: 10.5px;
}

/* menu row */
.menu {
    display: flex;
    align-items: center;
    gap: 2px;
    flex: 1;
    min-width: 0;
}
.groep {
    position: relative;
}
.menu-knop {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 40px;
    padding: 0 var(--s-3);
    border-radius: var(--r-md);
    background: none;
    border: 0;
    cursor: pointer;
    color: var(--muted);
    font-family: var(--body);
    font-size: 14.5px;
    font-weight: 600;
    text-decoration: none;
    transition: 0.15s;
    white-space: nowrap;
}
.pijl-wrap {
    display: inline-flex;
    width: 14px;
    height: 14px;
    transition: transform 0.15s;
}
.pijl-wrap :deep(svg) {
    width: 14px;
    height: 14px;
}
.menu-knop:hover {
    background: rgba(255, 255, 255, 0.07);
    color: #fff;
}
.menu-knop[aria-expanded="true"] {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
}
.menu-knop[aria-expanded="true"] .pijl-wrap {
    transform: rotate(180deg);
}
.menu-knop.actief,
.menu-knop.actief:hover {
    background: var(--paper);
    color: var(--violet-700);
}

/* dropdown */
.uitklap {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    min-width: 260px;
    z-index: 70;
    background: var(--menu);
    border: 1px solid var(--line-strong);
    border-radius: var(--r-lg);
    padding: var(--s-2);
    box-shadow: 0 30px 60px -20px #000;
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.uitklap a {
    display: flex;
    gap: var(--s-3);
    align-items: flex-start;
    padding: 10px var(--s-3);
    border-radius: var(--r-md);
    color: var(--text);
    text-decoration: none;
    cursor: pointer;
    transition: 0.12s;
}
.uitklap a:hover {
    background: rgba(255, 255, 255, 0.08);
}
.uitklap a[aria-current="page"] {
    background: var(--paper);
    color: var(--violet-700);
}
.uitklap a[aria-current="page"] .sub {
    color: var(--violet-600);
}
.uitklap .ico {
    width: 18px;
    height: 18px;
    flex: none;
    margin-top: 2px;
    color: var(--muted);
    display: inline-flex;
}
.uitklap a[aria-current="page"] .ico {
    color: var(--violet-600);
}
.uitklap .ico :deep(svg) {
    width: 18px;
    height: 18px;
}
.uitklap .t {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.uitklap .naam {
    font-weight: 600;
    font-size: 14.5px;
}
.uitklap .sub {
    font-size: 12.5px;
    color: var(--muted);
}

/* right side */
.top-rechts {
    display: flex;
    align-items: center;
    gap: var(--s-3);
    flex: none;
}
.nieuw {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-family: var(--body);
    font-weight: 600;
    font-size: 13px;
    border-radius: var(--r-pill);
    padding: 9px 14px;
    background: var(--pink);
    color: #fff;
    box-shadow: 0 10px 24px -12px var(--pink);
    white-space: nowrap;
    transition: 0.15s;
}
.nieuw:hover {
    background: var(--pink-hi);
    transform: translateY(-1px);
}
.nieuw .ico {
    display: inline-flex;
    width: 16px;
    height: 16px;
}
.nieuw .ico :deep(svg) {
    width: 16px;
    height: 16px;
}

.wie {
    position: relative;
}
.wie-knop {
    display: flex;
    align-items: center;
    gap: var(--s-2);
    background: none;
    border: 1px solid var(--line);
    border-radius: var(--r-pill);
    padding: 4px 10px 4px 4px;
    color: var(--text);
    cursor: pointer;
    font-family: var(--body);
    font-size: 13.5px;
    font-weight: 600;
}
.wie-knop:hover {
    border-color: var(--line-strong);
}
.avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--violet-500);
    display: grid;
    place-items: center;
    font-family: var(--num);
    font-size: 11px;
    font-weight: 700;
    color: #fff;
}
.uitklap.rechts {
    left: auto;
    right: 0;
    min-width: 200px;
}
.uitklap .kop {
    padding: 10px var(--s-3);
    font-size: 13px;
    color: var(--muted);
    border-bottom: 1px solid var(--line);
    margin-bottom: var(--s-1);
}
.uitklap .kop b {
    display: block;
    color: var(--text);
    font-size: 14px;
}
.uitklap .kop span {
    display: block;
}

/* Log out sits in the account menu as a full-width menu button. */
.logout-knop {
    justify-content: flex-start;
    width: 100%;
    gap: var(--s-2);
}

.hamburger {
    display: none;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 0;
    background: none;
    color: var(--muted);
    cursor: pointer;
}
.hamburger .ico {
    width: 24px;
    height: 24px;
    display: inline-flex;
}
.hamburger .ico :deep(svg) {
    width: 24px;
    height: 24px;
}

@media (max-width: 980px) {
    .top-in {
        gap: var(--s-3);
    }
    .hamburger {
        display: inline-flex;
        margin-left: auto;
    }
    .wie-knop .mail {
        display: none;
    }
    /* Shrink the New booking button to its + icon so the hamburger always fits. */
    .nieuw .lang {
        display: none;
    }
    /* The menu is a full-width panel that overlays the page, dropping down from
       under the (fixed) top bar — not an item that grows the bar itself. */
    .menu {
        display: none;
        position: absolute;
        left: 0;
        right: 0;
        top: 64px;
        flex-direction: column;
        align-items: stretch;
        gap: 2px;
        background: var(--menu);
        border-bottom: 1px solid var(--line-strong);
        padding: var(--s-3) max(16px, env(safe-area-inset-left, 0px)) var(--s-4);
        max-height: calc(100vh - 64px);
        overflow: auto;
        box-shadow: 0 30px 60px -30px #000;
    }
    .menu.open {
        display: flex;
    }
    .menu-knop {
        width: 100%;
        justify-content: space-between;
        height: 44px;
    }
    .menu .uitklap {
        position: static;
        box-shadow: none;
        border: 0;
        background: transparent;
        padding: 0 0 var(--s-2) var(--s-3);
        min-width: 0;
    }
    .wie .uitklap {
        right: 0;
        left: auto;
        max-width: calc(100vw - 16px);
    }
}
</style>
